import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

interface LogoutDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const LogoutDialog: React.FC<LogoutDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const theme = useTheme();

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={!isLoading ? onClose : undefined}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: "blur(20px)",
              overflow: "hidden",
              boxShadow: `0 8px 32px ${alpha(
                theme.palette.common.black,
                0.08
              )}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            },
            component: motion.div,
            initial: { opacity: 0, scale: 0.95 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 0.95 },
            transition: {
              duration: 0.2,
              ease: "easeOut",
            },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            sx={{
              p: 4,
              background: `linear-gradient(135deg, ${alpha(
                "#1E8078",
                0.03
              )} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                mb: 4,
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 200,
                  damping: 15,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "50%",
                    backgroundColor: alpha("#1E8078", 0.08),
                    color: "#1E8078",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: -4,
                      left: -4,
                      right: -4,
                      bottom: -4,
                      borderRadius: "50%",
                      border: `2px solid ${alpha("#1E8078", 0.12)}`,
                      transition: "all 0.2s ease-in-out",
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 44 }}>logout</Icon>
                </Paper>
              </motion.div>

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h5"
                  component={motion.h5}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: "text.primary",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Confirm Logout
                </Typography>
                <Typography
                  variant="body1"
                  component={motion.p}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  sx={{
                    color: alpha(theme.palette.text.secondary, 0.8),
                    maxWidth: 320,
                    mx: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  Are you sure you want to log out? Any unsaved changes will be
                  lost.
                </Typography>
              </Box>
            </Box>

            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                disabled={isLoading}
                sx={{
                  borderRadius: 2,
                  py: 1.4,
                  borderColor: alpha(theme.palette.divider, 0.15),
                  color: theme.palette.text.secondary,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  backdropFilter: "blur(10px)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: alpha(theme.palette.divider, 0.3),
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={onConfirm}
                disabled={isLoading}
                startIcon={isLoading ? <Icon>sync</Icon> : <Icon>logout</Icon>}
                sx={{
                  borderRadius: 2,
                  py: 1.4,
                  bgcolor: "#1E8078",
                  boxShadow: `0 4px 12px ${alpha("#1E8078", 0.2)}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: alpha("#1E8078", 0.9),
                    boxShadow: `0 6px 16px ${alpha("#1E8078", 0.3)}`,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                  "& .MuiIcon-root": {
                    animation: isLoading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  },
                }}
              >
                {isLoading ? "Logging out..." : "Logout"}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </AnimatePresence>
  );
};
